// src/pages/kpis/compras/PreciosMP.jsx
// KPI de Variabilidad de Precios — pestañas Resumen / Histórico / Detalle / Catálogo.
//
// Reglas del KPI (acordadas con Compras):
//  - Fuente: líneas de órdenes de compra (NetSuite customsearch743), clases
//    Materia Prima, Material Empaque y Material Embalaje.
//  - Precio de un artículo en un mes = importe en MXN / cantidad en unidad base
//    (kg, L o pieza), ponderado entre todos sus proveedores.
//  - Variación = contra el último mes en que hubo compra de ese artículo.
//  - Tarjeta principal: artículos del top (prioridad ALTA), variación
//    ponderada por lo gastado en cada uno. Segunda tarjeta: fuera del top.
//  - No cuentan: compras para reventa, exclusiones manuales, OCs en moneda
//    extranjera con tipo de cambio 1, servicios y líneas en 0.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import * as XLSX from 'xlsx';
import { FiDownload, FiRefreshCw, FiInfo, FiChevronLeft, FiChevronRight, FiAlertTriangle, FiTrash2, FiPlus } from 'react-icons/fi';
import api from '../../../services/api';
import s from '../../mantenimiento/estilos';

Chart.register(...registerables);

const BASE = '/api/kpis/precios-mp';
const COLOR = { verde: '#3E7C5A', amarillo: '#B8862E', rojo: '#B23A48', morado: '#4A1E5C', lila: '#A98BB8', gris: '#8A7690' };
const PALETA_PROVEEDORES = ['#B8862E', '#3E7C5A', '#6E9BC5', '#C2627A', '#7A6BB0', '#5FA39A', '#D08C4F'];
const CLASES = ['Materia Prima', 'Material Empaque', 'Material Embalaje'];

// ─── Helpers ───
const num = (v) => (v === null || v === undefined || v === '' ? null : parseFloat(v));
const fmtNum = (v, dec = 2) => (num(v) === null ? '—' : num(v).toLocaleString('es-MX', { maximumFractionDigits: dec }));
const fmtMoneda = (v, dec = 2) =>
  num(v) === null ? '—' : num(v).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtVar = (v) => (num(v) === null ? '—' : `${num(v) > 0 ? '+' : ''}${num(v).toFixed(2)}%`);
const colorVar = (v) => (num(v) === null || num(v) === 0 ? COLOR.gris : num(v) > 0 ? COLOR.rojo : COLOR.verde);
const fmtFecha = (d) => (d ? new Date(d).toLocaleDateString('es-MX') : '—');
const nombreMes = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }) : '—');
const nombreMesCorto = (d) => new Date(d).toLocaleDateString('es-MX', { month: 'short', year: '2-digit' });
const pad = (n) => String(n).padStart(2, '0');

function rangoDeMes(mes) {
  const d = new Date(mes);
  const y = d.getFullYear();
  const m = d.getMonth();
  return { desde: `${y}-${pad(m + 1)}-01`, hasta: `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}` };
}

/** Semáforo de variación: subir es malo. verde ≤2 · amarillo ≤5 · rojo >5 */
function semaforoVar(v) {
  if (num(v) === null) return 'gris';
  if (num(v) <= 2) return 'verde';
  if (num(v) <= 5) return 'amarillo';
  return 'rojo';
}

function descargarExcel(filas, hoja, archivo) {
  const ws = XLSX.utils.json_to_sheet(filas);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, hoja);
  XLSX.writeFile(wb, archivo);
}

function Grafica({ config, alto = 280 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = new Chart(ref.current, config);
    return () => chart.destroy();
  }, [config]);
  return (
    <div style={{ position: 'relative', height: alto }}>
      <canvas ref={ref} />
    </div>
  );
}

const Tag = ({ children, tipo = 'gris', title }) => (
  <span style={{ ...x.tag, ...x[`tag_${tipo}`] }} title={title}>{children}</span>
);

export default function PreciosMP() {
  const [tab, setTab] = useState('resumen');
  const [resumen, setResumen] = useState([]);
  const [ultimaSync, setUltimaSync] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [catalogo, setCatalogo] = useState([]);

  // Resumen: tabla de artículos del mes
  const [mesSel, setMesSel] = useState(null);
  const [alcance, setAlcance] = useState('top');
  const [claseSel, setClaseSel] = useState('');
  const [articulos, setArticulos] = useState([]);
  const [cargandoArticulos, setCargandoArticulos] = useState(false);

  // Histórico de un artículo
  const [articuloSel, setArticuloSel] = useState(null); // { id_articulo, articulo }
  const [historial, setHistorial] = useState(null);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);

  // Detalle de líneas
  const filtrosVacios = { desde: '', hasta: '', alcance: 'top', clase: '', busqueda: '', id_articulo: '' };
  const [filtros, setFiltros] = useState(filtrosVacios);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(50);
  const [lineas, setLineas] = useState({ data: [], total: 0, totalPaginas: 1 });
  const [cargandoLineas, setCargandoLineas] = useState(false);
  const [exportando, setExportando] = useState(false);

  // ─── Cargas ───
  async function cargarResumen() {
    const res = await api.get(`${BASE}/resumen`);
    setResumen(res.data.data);
    setUltimaSync(res.data.ultimaSync);
    return res.data.data;
  }

  async function cargarCatalogo() {
    const res = await api.get(`${BASE}/catalogo`);
    setCatalogo(res.data.data);
    return res.data.data;
  }

  async function cargarArticulos(mes = mesSel, alc = alcance, clase = claseSel) {
    if (!mes) return;
    try {
      setCargandoArticulos(true);
      const params = { mes: rangoDeMes(mes).desde, alcance: alc };
      if (alc === 'todos' && clase) params.clase = clase;
      const res = await api.get(`${BASE}/articulos`, { params });
      setArticulos(res.data.data);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudieron cargar los artículos del mes.' });
    } finally {
      setCargandoArticulos(false);
    }
  }

  async function cargarHistorial(art) {
    if (!art) return;
    try {
      setCargandoHistorial(true);
      const res = await api.get(`${BASE}/articulo/${art.id_articulo}/historial`);
      setHistorial(res.data);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo cargar el historial del artículo.' });
    } finally {
      setCargandoHistorial(false);
    }
  }

  async function cargarLineas(f = filtros, p = pagina, pp = porPagina) {
    try {
      setCargandoLineas(true);
      const params = { pagina: p, porPagina: pp };
      Object.entries(f).forEach(([k, v]) => { if (v && !(k === 'alcance' && v === 'todos')) params[k] = v; });
      const res = await api.get(`${BASE}/lineas`, { params });
      setLineas({ data: res.data.data, total: res.data.total, totalPaginas: res.data.totalPaginas });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudieron cargar las líneas de OC.' });
    } finally {
      setCargandoLineas(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        setCargando(true);
        const [filas, cat] = await Promise.all([cargarResumen(), cargarCatalogo()]);
        const cerrado = filas.find((r) => !r.es_parcial) || filas[0];
        if (cerrado) {
          setMesSel(cerrado.mes);
          await cargarArticulos(cerrado.mes, 'top');
          const f = { ...filtrosVacios, ...rangoDeMes(cerrado.mes) };
          setFiltros(f);
          await cargarLineas(f, 1, porPagina);
        }
        const primero = cat.find((c) => c.activo);
        if (primero) {
          const art = { id_articulo: primero.id_articulo, articulo: primero.articulo };
          setArticuloSel(art);
          await cargarHistorial(art);
        }
        setError(null);
      } catch (err) {
        console.error(err);
        setError('No se pudo cargar el KPI de Variabilidad de Precios.');
      } finally {
        setCargando(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSincronizar() {
    try {
      setSincronizando(true);
      setMensaje(null);
      const res = await api.post('/api/kpis/sync/precios-mp');
      await Promise.all([cargarResumen(), cargarArticulos(), cargarLineas(), articuloSel ? cargarHistorial(articuloSel) : null]);
      const r = res.data.resultado;
      setMensaje({ tipo: 'ok', texto: `Sincronizado con NetSuite: ${r.insertados} líneas nuevas, ${r.actualizados} actualizadas.` });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo sincronizar con NetSuite.' });
    } finally {
      setSincronizando(false);
    }
  }

  function verHistorial(art) {
    setArticuloSel(art);
    setTab('historico');
    cargarHistorial(art);
  }

  function verLineasDe(art, mes) {
    const f = { ...filtrosVacios, alcance: 'todos', id_articulo: String(art.id_articulo), ...(mes ? rangoDeMes(mes) : {}) };
    setFiltros(f);
    setPagina(1);
    setTab('detalle');
    cargarLineas(f, 1);
  }

  // ─── Derivados ───
  const ordenAsc = useMemo(() => [...resumen].reverse(), [resumen]);
  const mesCerrado = resumen.find((r) => !r.es_parcial) || null;
  const idxCerrado = mesCerrado ? resumen.indexOf(mesCerrado) : -1;
  const previoCerrado = idxCerrado >= 0 ? resumen.slice(idxCerrado + 1).find((r) => !r.es_parcial && r.variacion_top !== null) : null;
  const mesEnCurso = resumen.find((r) => r.es_parcial) || null;

  const configVariacionMensual = useMemo(() => ({
    type: 'bar',
    data: {
      labels: ordenAsc.map((r) => nombreMesCorto(r.mes) + (r.es_parcial ? '*' : '')),
      datasets: [
        {
          label: 'Top (ponderado)',
          data: ordenAsc.map((r) => num(r.variacion_top)),
          backgroundColor: ordenAsc.map((r) => (r.es_parcial ? '#D9CDE0' : COLOR[semaforoVar(r.variacion_top)])),
        },
        {
          type: 'line',
          label: 'Fuera del top (ponderado)',
          data: ordenAsc.map((r) => num(r.variacion_fuera_top)),
          borderColor: COLOR.lila,
          backgroundColor: COLOR.lila,
          borderDash: [5, 4],
          pointRadius: 3,
        },
      ],
    },
    options: {
      maintainAspectRatio: false,
      scales: { y: { ticks: { callback: (v) => `${v}%` }, title: { display: true, text: 'Variación vs mes anterior con compra' } } },
      plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${fmtVar(c.raw)}` } } },
    },
  }), [ordenAsc]);

  const configPrecioArticulo = useMemo(() => {
    if (!historial) return null;
    const serie = historial.serie;
    const labels = serie.map((p) => nombreMesCorto(p.mes) + (p.es_parcial ? '*' : ''));
    const proveedores = [...new Set(historial.proveedores.map((p) => p.proveedor))];
    const datasetsProv = proveedores.map((prov, i) => {
      const porMes = Object.fromEntries(
        historial.proveedores.filter((p) => p.proveedor === prov).map((p) => [rangoDeMes(p.mes).desde, num(p.precio)])
      );
      return {
        label: prov,
        data: serie.map((p) => porMes[rangoDeMes(p.mes).desde] ?? null),
        borderColor: PALETA_PROVEEDORES[i % PALETA_PROVEEDORES.length],
        backgroundColor: PALETA_PROVEEDORES[i % PALETA_PROVEEDORES.length],
        borderWidth: 1.5,
        pointRadius: 3,
        showLine: false,
      };
    });
    return {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Precio ponderado del mes',
            data: serie.map((p) => num(p.precio)),
            borderColor: COLOR.morado,
            backgroundColor: COLOR.morado,
            borderWidth: 2.5,
            pointRadius: serie.map((p) => (p.revisar ? 7 : 5)),
            pointBackgroundColor: serie.map((p) => (p.revisar ? COLOR.amarillo : COLOR.morado)),
            spanGaps: true,
            segment: { borderDash: (ctx) => (serie[ctx.p1DataIndex] && serie[ctx.p0DataIndex + 1]?.sin_compra ? [4, 4] : undefined) },
          },
          ...datasetsProv,
        ],
      },
      options: {
        maintainAspectRatio: false,
        scales: { y: { ticks: { callback: (v) => fmtMoneda(v, 0) } } },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${fmtMoneda(c.raw)}` } },
        },
      },
    };
  }, [historial]);

  // ─── Excel ───
  function excelArticulos() {
    descargarExcel(
      articulos.map((a) => ({
        'Artículo': a.articulo,
        Prioridad: a.prioridad || '',
        Clase: a.clase || '',
        [`Precio ${nombreMes(mesSel)} (MXN)`]: a.sin_compra ? null : num(a.precio),
        Unidad: a.unidad_base || '',
        'Precio anterior (MXN)': num(a.precio_anterior),
        'Mes anterior con compra': a.mes_anterior ? nombreMes(a.mes_anterior) : '',
        'Variación %': num(a.pct_variacion),
        'Gasto del mes (MXN)': num(a.importe),
        'Cantidad comprada': num(a.cantidad),
        Proveedores: a.proveedores ?? '',
        Observación: a.sin_compra
          ? `Sin compra — último precio ${fmtMoneda(a.ultimo_precio)} en ${nombreMes(a.ultimo_mes)}`
          : a.revisar ? 'Brinco ≥50%: revisar captura' : '',
      })),
      'Artículos',
      `Precios_${alcance}_${rangoDeMes(mesSel).desde.slice(0, 7)}.xlsx`
    );
  }

  function excelHistorial() {
    if (!historial) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(historial.serie.map((p) => ({
      Mes: nombreMes(p.mes) + (p.es_parcial ? ' (parcial)' : ''),
      'Precio ponderado (MXN)': num(p.precio),
      Unidad: p.unidad_base || '',
      'Precio anterior (MXN)': num(p.precio_anterior),
      'Mes anterior con compra': p.mes_anterior ? nombreMes(p.mes_anterior) : '',
      'Variación %': num(p.pct_variacion),
      'Gasto (MXN)': num(p.importe),
      Cantidad: num(p.cantidad),
      Proveedores: p.proveedores ?? '',
      Observación: p.sin_compra ? 'Sin compra' : p.revisar ? 'Brinco ≥50%: revisar captura' : '',
    }))), 'Por mes');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(historial.proveedores.map((p) => ({
      Proveedor: p.proveedor,
      Mes: nombreMes(p.mes),
      'Precio (MXN)': num(p.precio),
      'Su precio anterior (MXN)': num(p.precio_anterior),
      'Su compra anterior': p.mes_anterior ? nombreMes(p.mes_anterior) : '',
      'Variación %': num(p.pct_variacion),
      Moneda: p.moneda,
      Cantidad: num(p.cantidad),
      'Gasto (MXN)': num(p.importe),
    }))), 'Por proveedor');
    XLSX.writeFile(wb, `Historial_precio_${(articuloSel?.articulo || 'articulo').replace(/[^\w]+/g, '_')}.xlsx`);
  }

  async function excelLineas() {
    try {
      setExportando(true);
      const params = { exportar: '1' };
      Object.entries(filtros).forEach(([k, v]) => { if (v && !(k === 'alcance' && v === 'todos')) params[k] = v; });
      const res = await api.get(`${BASE}/lineas`, { params });
      descargarExcel(
        res.data.data.map((l) => ({
          Fecha: fmtFecha(l.fecha),
          OC: l.numero_documento,
          Proveedor: l.proveedor,
          'Artículo': l.articulo,
          Clase: l.clase || '',
          Prioridad: l.prioridad || '',
          'Cantidad (unidad base)': num(l.cantidad),
          'Unidad de la OC': l.unidades || '',
          'Precio MXN': num(l.precio_unitario),
          Moneda: l.moneda,
          'Precio moneda original': num(l.precio_moneda_original),
          'Tipo de cambio': num(l.tipo_cambio),
          'Importe MXN': num(l.importe),
          '¿Cuenta para el KPI?': l.excluida || l.motivo_no_cuenta ? 'No' : 'Sí',
          Motivo: l.excluida ? l.motivo_exclusion : l.motivo_no_cuenta || '',
        })),
        'Líneas OC',
        `Precios_lineas_OC${filtros.desde ? `_${filtros.desde}_a_${filtros.hasta}` : ''}.xlsx`
      );
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo generar el Excel.' });
    } finally {
      setExportando(false);
    }
  }

  // ─── Acciones de escritura ───
  async function toggleExclusion(linea) {
    try {
      let body;
      if (linea.excluida) {
        body = { excluir: false };
      } else {
        const motivo = window.prompt(`¿Por qué no debe contar esta línea?\nOC ${linea.numero_documento} · ${linea.articulo}`, 'Compra para reventa');
        if (!motivo || !motivo.trim()) return;
        body = { excluir: true, motivo };
      }
      await api.patch(`${BASE}/lineas/${linea.id_linea}/exclusion`, body);
      await Promise.all([cargarLineas(), cargarResumen(), cargarArticulos()]);
      setMensaje({ tipo: 'ok', texto: linea.excluida ? 'La línea vuelve a contar para el KPI.' : 'Línea excluida del KPI.' });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.status === 403 ? 'No tienes permiso de captura en Compras.' : err.response?.data?.error || 'No se pudo guardar.' });
    }
  }

  if (cargando) return <div style={s.paginaContenedor}>Cargando...</div>;
  if (error) return <div style={{ ...s.paginaContenedor, color: COLOR.rojo }}>{error}</div>;

  return (
    <div style={s.paginaContenedor} className="contenedor-captura">
      <div style={{ ...s.headerFila, flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div>
          <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>Variabilidad de Precios</h1>
          <div style={{ fontSize: 12, color: COLOR.gris }}>
            Última sincronización con NetSuite: {ultimaSync ? new Date(ultimaSync).toLocaleString('es-MX') : 'sin registro'}
          </div>
        </div>
        <button onClick={handleSincronizar} disabled={sincronizando} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiRefreshCw /> {sincronizando ? 'Sincronizando...' : 'Sincronizar con NetSuite'}
        </button>
      </div>

      {mensaje && <div style={{ ...s.mensaje, color: mensaje.tipo === 'ok' ? COLOR.verde : COLOR.rojo }}>{mensaje.texto}</div>}

      <div style={s.tabBar}>
        {[['resumen', 'Resumen'], ['historico', 'Histórico'], ['detalle', 'Detalle de OCs'], ['catalogo', 'Catálogo del top']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...s.tabBtn, ...(tab === k ? s.tabBtnActivo : {}) }}>{label}</button>
        ))}
      </div>

      {/* ═══════════ RESUMEN ═══════════ */}
      {tab === 'resumen' && (
        <>
          <div style={s.kgrid}>
            {mesCerrado && <TarjetaTop fila={mesCerrado} previo={previoCerrado} />}
            {mesCerrado && <TarjetaFueraTop fila={mesCerrado} onVer={() => { setAlcance('todos'); cargarArticulos(mesSel, 'todos'); }} />}
            {mesEnCurso && <TarjetaEnCurso fila={mesEnCurso} />}
          </div>

          <div style={{ ...s.filtrosBar, alignItems: 'center' }}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Mes</span>
              <select
                style={s.filtroInput}
                value={mesSel ? rangoDeMes(mesSel).desde : ''}
                onChange={(e) => { const m = resumen.find((r) => rangoDeMes(r.mes).desde === e.target.value)?.mes; setMesSel(m); cargarArticulos(m); }}
              >
                {resumen.map((r) => (
                  <option key={r.mes} value={rangoDeMes(r.mes).desde}>{nombreMes(r.mes)}{r.es_parcial ? ' (parcial)' : ''}</option>
                ))}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Artículos</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {[['top', 'Solo el top'], ['todos', 'Todos con compra']].map(([k, label]) => (
                  <button key={k} onClick={() => { setAlcance(k); cargarArticulos(mesSel, k); }} style={{ ...s.subTabBtn, ...(alcance === k ? s.subTabBtnActivo : {}) }}>{label}</button>
                ))}
              </div>
            </div>
            {alcance === 'todos' && (
              <div style={s.filtroGrupo}>
                <span style={s.filtroLabel}>Clase</span>
                <select style={s.filtroInput} value={claseSel} onChange={(e) => { setClaseSel(e.target.value); cargarArticulos(mesSel, 'todos', e.target.value); }}>
                  <option value="">Todas</option>
                  {CLASES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            <button onClick={excelArticulos} disabled={articulos.length === 0} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
              <FiDownload /> Descargar Excel
            </button>
          </div>

          <div style={s.subtitulo}>
            {alcance === 'top' ? 'Artículos del catálogo del top.' : 'Todos los artículos con compra en el mes, del que más subió al que más bajó.'}{' '}
            Haz clic en un artículo para ver su historial.
          </div>

          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Artículo</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Precio del mes</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Precio anterior</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Variación</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Gasto del mes</th>
                  <th style={s.th}>Proveedores</th>
                  <th style={s.th}>Notas</th>
                </tr>
              </thead>
              <tbody>
                {cargandoArticulos && <tr><td colSpan={7} style={{ ...s.td, color: COLOR.gris }}>Cargando...</td></tr>}
                {!cargandoArticulos && articulos.length === 0 && (
                  <tr><td colSpan={7} style={{ ...s.td, color: COLOR.gris, padding: 16 }}>No hay artículos para este mes.</td></tr>
                )}
                {!cargandoArticulos && articulos.map((a) => (
                  <tr key={a.id_articulo} onClick={() => verHistorial(a)} style={{ cursor: 'pointer', ...(a.sin_compra ? { background: '#FBF8F3' } : {}) }}>
                    <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 180 }}>
                      {a.articulo}
                      {a.prioridad && <Tag tipo={a.prioridad === 'ALTA' ? 'morado' : 'gris'}>{a.prioridad}</Tag>}
                      {alcance === 'todos' && a.clase && a.clase !== 'Materia Prima' && <Tag>{a.clase.replace('Material ', '')}</Tag>}
                    </td>
                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>
                      {a.sin_compra ? <span style={{ color: COLOR.gris, fontWeight: 400 }}>—</span> : <>{fmtMoneda(a.precio)}<span style={x.unidad}>/{a.unidad_base}</span></>}
                    </td>
                    <td style={{ ...s.td, textAlign: 'right', color: COLOR.gris }}>
                      {a.sin_compra
                        ? <>{fmtMoneda(a.ultimo_precio)} <span style={x.unidad}>({a.ultimo_mes ? nombreMesCorto(a.ultimo_mes) : '—'})</span></>
                        : a.precio_anterior ? <>{fmtMoneda(a.precio_anterior)} <span style={x.unidad}>({nombreMesCorto(a.mes_anterior)})</span></> : '—'}
                    </td>
                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: colorVar(a.pct_variacion) }}>{a.sin_compra ? '—' : fmtVar(a.pct_variacion)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{a.sin_compra ? '—' : fmtMoneda(a.importe, 0)}</td>
                    <td style={s.td}>{a.proveedores ?? '—'}</td>
                    <td style={{ ...s.td, whiteSpace: 'normal' }}>
                      {a.sin_compra && <Tag title="Se compara contra el último mes con compra">Sin compra este mes</Tag>}
                      {a.revisar && <Tag tipo="amarillo" title="Casi siempre es error de captura (unidad, cantidad o tipo de cambio)"><FiAlertTriangle size={10} /> Revisar</Tag>}
                      {a.tiene_moneda_extranjera && <Tag tipo="azul">USD</Tag>}
                      {num(a.proveedores) > 1 && <Tag title="El precio mezcla varios proveedores; ve el desglose en Histórico">Varios proveedores</Tag>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ ...s.seccionKpiBloque, marginTop: 18 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <FiInfo style={{ color: COLOR.morado, marginTop: 2, flexShrink: 0 }} />
              <div style={s.seccionKpiFormula}>
                <strong style={{ color: '#2A1830' }}>Cómo se calcula.</strong> El precio de un artículo en el mes es lo que se pagó en pesos entre la cantidad comprada,
                en su unidad base (kg, litro o pieza), juntando a todos los proveedores. Las compras en dólares se convierten con el tipo de cambio de su OC.
                La variación compara contra el último mes en que se compró ese artículo. La tarjeta del top promedia la variación de los artículos de prioridad ALTA,
                dando más peso a los que más se gastaron en el mes. Verde hasta +2%, amarillo hasta +5%, rojo arriba de +5%.
                <br />
                <strong style={{ color: '#2A1830' }}>No cuentan:</strong> compras para reventa, líneas excluidas a mano, OCs en dólares con tipo de cambio 1, servicios y líneas en cero.
                <br />
                <strong style={{ color: '#2A1830' }}>De dónde sale.</strong> NetSuite, búsqueda guardada "líneas de OC" (customsearch743). Cada sincronización actualiza el mes en curso y los dos anteriores.
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════ HISTÓRICO ═══════════ */}
      {tab === 'historico' && (
        <>
          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>Variación mensual del KPI</div>
            <Grafica config={configVariacionMensual} alto={260} />
            <div style={{ ...s.kSub, marginTop: 8 }}>* Mes en curso, todavía parcial.</div>
          </div>

          <div style={s.tarjeta}>
            <div style={{ ...s.headerFila, marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
              <div style={{ ...s.seccionTitulo, marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>Precio por mes de un artículo</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <SelectorArticulo catalogo={catalogo} seleccionado={articuloSel} onElegir={(a) => { setArticuloSel(a); cargarHistorial(a); }} />
                <button onClick={excelHistorial} disabled={!historial} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <FiDownload /> Excel
                </button>
              </div>
            </div>

            {cargandoHistorial && <div style={{ color: COLOR.gris, fontSize: 12 }}>Cargando...</div>}
            {!cargandoHistorial && historial && (
              <>
                <Grafica config={configPrecioArticulo} alto={300} />
                <div style={{ ...s.kSub, marginTop: 8 }}>
                  Línea morada: precio ponderado del mes. Puntos de colores: precio de cada proveedor. Punto amarillo: brinco de 50% o más, revisar captura.
                  Tramo punteado: meses sin compra.
                </div>

                <h3 style={{ ...s.subtituloLista, margin: '18px 0 8px' }}>Por mes</h3>
                <div style={s.tablaScroll}>
                  <table style={s.tabla}>
                    <thead>
                      <tr>
                        <th style={s.th}>Mes</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>Precio</th>
                        <th style={s.th}>Contra</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>Variación</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>Cantidad</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>Gasto</th>
                        <th style={s.th}>Proveedores</th>
                        <th style={s.th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...historial.serie].reverse().map((p) => (
                        <tr key={p.mes} style={p.sin_compra ? { background: '#FBF8F3' } : undefined}>
                          <td style={s.td}>{nombreMes(p.mes)}{p.es_parcial && <Tag tipo="amarillo">parcial</Tag>}</td>
                          {p.sin_compra ? (
                            <td colSpan={6} style={{ ...s.td, color: COLOR.gris }}>Sin compra</td>
                          ) : (
                            <>
                              <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{fmtMoneda(p.precio)}<span style={x.unidad}>/{p.unidad_base}</span></td>
                              <td style={{ ...s.td, color: COLOR.gris }}>{p.mes_anterior ? `${fmtMoneda(p.precio_anterior)} (${nombreMesCorto(p.mes_anterior)})` : 'Primera compra'}</td>
                              <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: colorVar(p.pct_variacion) }}>
                                {fmtVar(p.pct_variacion)} {p.revisar && <Tag tipo="amarillo"><FiAlertTriangle size={10} /> Revisar</Tag>}
                              </td>
                              <td style={{ ...s.td, textAlign: 'right' }}>{fmtNum(p.cantidad)} {p.unidad_base}</td>
                              <td style={{ ...s.td, textAlign: 'right' }}>{fmtMoneda(p.importe, 0)}</td>
                              <td style={s.td}>{p.proveedores}</td>
                            </>
                          )}
                          <td style={s.td}>
                            {!p.sin_compra && <button style={s.linkChico} onClick={() => verLineasDe(articuloSel, p.mes)}>Ver OCs</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 style={{ ...s.subtituloLista, margin: '18px 0 4px' }}>Por proveedor</h3>
                <div style={s.subtitulo}>Cada proveedor contra su propia compra anterior.</div>
                <div style={s.tablaScroll}>
                  <table style={s.tabla}>
                    <thead>
                      <tr>
                        <th style={s.th}>Proveedor</th>
                        <th style={s.th}>Mes</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>Precio</th>
                        <th style={s.th}>Su compra anterior</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>Variación</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>Cantidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...historial.proveedores].sort((a, b) => new Date(b.mes) - new Date(a.mes) || a.proveedor.localeCompare(b.proveedor)).map((p) => (
                        <tr key={`${p.proveedor}-${p.mes}`}>
                          <td style={{ ...s.td, whiteSpace: 'normal' }}>{p.proveedor}{p.moneda && p.moneda !== 'MXN' && <Tag tipo="azul">{p.moneda}</Tag>}</td>
                          <td style={s.td}>{nombreMes(p.mes)}</td>
                          <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{fmtMoneda(p.precio)}</td>
                          <td style={{ ...s.td, color: COLOR.gris }}>{p.mes_anterior ? `${fmtMoneda(p.precio_anterior)} (${nombreMesCorto(p.mes_anterior)})` : 'Primera compra'}</td>
                          <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: colorVar(p.pct_variacion) }}>{fmtVar(p.pct_variacion)}</td>
                          <td style={{ ...s.td, textAlign: 'right' }}>{fmtNum(p.cantidad)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ═══════════ DETALLE ═══════════ */}
      {tab === 'detalle' && (
        <>
          <div style={s.filtrosBar}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Mes</span>
              <select
                style={s.filtroInput}
                value={resumen.some((r) => { const g = rangoDeMes(r.mes); return g.desde === filtros.desde && g.hasta === filtros.hasta; }) ? filtros.desde : ''}
                onChange={(e) => setFiltros(e.target.value ? { ...filtros, ...rangoDeMes(e.target.value + 'T00:00:00') } : { ...filtros, desde: '', hasta: '' })}
              >
                <option value="">Rango personalizado</option>
                {resumen.map((r) => <option key={r.mes} value={rangoDeMes(r.mes).desde}>{nombreMes(r.mes)}{r.es_parcial ? ' (parcial)' : ''}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Desde</span>
              <input type="date" style={s.filtroInput} value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Hasta</span>
              <input type="date" style={s.filtroInput} value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Artículos</span>
              <select style={s.filtroInput} value={filtros.alcance} onChange={(e) => setFiltros({ ...filtros, alcance: e.target.value })}>
                <option value="top">Solo el top</option>
                <option value="todos">Todos</option>
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Clase</span>
              <select style={s.filtroInput} value={filtros.clase} onChange={(e) => setFiltros({ ...filtros, clase: e.target.value })}>
                <option value="">Todas</option>
                {CLASES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Buscar</span>
              <input
                type="text"
                placeholder="Artículo, proveedor u OC"
                style={{ ...s.filtroInput, minWidth: 190 }}
                value={filtros.busqueda}
                onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPagina(1); cargarLineas(filtros, 1); } }}
              />
            </div>
            <button style={s.botonBuscar2} onClick={() => { setPagina(1); cargarLineas(filtros, 1); }}>Buscar</button>
            <button style={s.linkChico} onClick={() => { setFiltros(filtrosVacios); setPagina(1); cargarLineas(filtrosVacios, 1); }}>Limpiar filtros</button>
          </div>

          {filtros.id_articulo && (
            <div style={{ ...s.avisoAdvertencia, marginTop: 0, marginBottom: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
              Mostrando solo: <strong>{lineas.data[0]?.articulo || `artículo ${filtros.id_articulo}`}</strong>
              <button style={s.linkChico} onClick={() => { const f = { ...filtros, id_articulo: '' }; setFiltros(f); setPagina(1); cargarLineas(f, 1); }}>quitar</button>
            </div>
          )}

          <div style={{ ...s.headerFila, marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 12, color: COLOR.gris }}>{cargandoLineas ? 'Cargando...' : `${lineas.total.toLocaleString('es-MX')} líneas de OC`}</div>
            <button onClick={excelLineas} disabled={exportando || lineas.total === 0} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiDownload /> {exportando ? 'Generando...' : 'Descargar Excel'}
            </button>
          </div>

          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Fecha</th>
                  <th style={s.th}>OC</th>
                  <th style={s.th}>Proveedor</th>
                  <th style={s.th}>Artículo</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Cantidad</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Precio MXN</th>
                  <th style={s.th}>Moneda</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Importe MXN</th>
                  <th style={s.th}>Para el KPI</th>
                  <th style={s.th}></th>
                </tr>
              </thead>
              <tbody>
                {!cargandoLineas && lineas.data.length === 0 && (
                  <tr><td colSpan={10} style={{ ...s.td, color: COLOR.gris, padding: 16 }}>No hay líneas con estos filtros.</td></tr>
                )}
                {lineas.data.map((l) => {
                  const noCuenta = l.excluida || !!l.motivo_no_cuenta;
                  return (
                    <tr key={l.id_linea} style={noCuenta ? { background: '#FDF3F1' } : undefined}>
                      <td style={s.td}>{fmtFecha(l.fecha)}</td>
                      <td style={s.td}>{l.numero_documento}</td>
                      <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 160 }}>{l.proveedor}</td>
                      <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 160 }}>
                        {l.articulo}
                        {l.prioridad && <Tag tipo={l.prioridad === 'ALTA' ? 'morado' : 'gris'}>{l.prioridad}</Tag>}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right' }}>
                        {fmtNum(l.cantidad)}
                        {l.unidades && !/^(kilos|litros|pieza)$/i.test(l.unidades) && <div style={x.unidad}>OC en {l.unidades}</div>}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{fmtMoneda(l.precio_unitario)}</td>
                      <td style={s.td}>
                        {l.moneda}
                        {l.moneda && l.moneda !== 'MXN' && (
                          <div style={x.unidad}>{fmtNum(l.precio_moneda_original, 4)} {l.moneda} · TC {fmtNum(l.tipo_cambio, 4)}</div>
                        )}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{fmtMoneda(l.importe)}</td>
                      <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 150 }}>
                        {l.excluida ? <Tag tipo="rojo" title="Excluida a mano">{l.motivo_exclusion}</Tag>
                          : l.motivo_no_cuenta ? <Tag tipo="rojo">{l.motivo_no_cuenta}</Tag>
                          : <span style={{ color: COLOR.verde, fontSize: 12 }}>Cuenta</span>}
                      </td>
                      <td style={s.td}>
                        {!l.motivo_no_cuenta && (
                          <button style={s.linkChico} onClick={() => toggleExclusion(l)}>{l.excluida ? 'Volver a contar' : 'Excluir'}</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={x.paginacion}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={s.filtroLabel}>Por página</span>
              <select style={s.inputChico} value={porPagina} onChange={(e) => { const pp = Number(e.target.value); setPorPagina(pp); setPagina(1); cargarLineas(filtros, 1, pp); }}>
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button style={x.botonPagina} disabled={pagina <= 1 || cargandoLineas} onClick={() => { const p = pagina - 1; setPagina(p); cargarLineas(filtros, p); }} aria-label="Página anterior"><FiChevronLeft /></button>
              <span style={{ fontSize: 12 }}>Página {pagina} de {lineas.totalPaginas}</span>
              <button style={x.botonPagina} disabled={pagina >= lineas.totalPaginas || cargandoLineas} onClick={() => { const p = pagina + 1; setPagina(p); cargarLineas(filtros, p); }} aria-label="Página siguiente"><FiChevronRight /></button>
            </div>
          </div>
        </>
      )}

      {/* ═══════════ CATÁLOGO ═══════════ */}
      {tab === 'catalogo' && (
        <Catalogo
          catalogo={catalogo}
          onCambio={async () => { await Promise.all([cargarCatalogo(), cargarResumen(), cargarArticulos()]); }}
          setMensaje={setMensaje}
        />
      )}
    </div>
  );
}

// ─── Tarjetas ───
function TarjetaTop({ fila, previo }) {
  const v = num(fila.variacion_top);
  const sem = semaforoVar(v);
  const vPrev = previo ? num(previo.variacion_top) : null;
  return (
    <div style={{ ...s.kcard, borderTop: `3px solid ${COLOR[sem]}` }}>
      <div style={s.kLbl}>Top · {nombreMes(fila.mes)}</div>
      <div style={{ ...s.kVal, fontSize: 32, color: COLOR[sem] }}>{fmtVar(v)}</div>
      <div style={{ ...x.veredicto, color: COLOR[sem] }}>
        {v === null ? 'Sin datos para comparar' : v <= 0 ? 'Los precios del top bajaron o se mantuvieron' : v <= 2 ? 'Subida dentro del rango aceptable' : v <= 5 ? 'Subida moderada, vigilar' : 'Subida fuerte de precios'}
      </div>
      <div style={s.kSub}>
        {fila.top_con_compra} de {fila.top_total} artículos del top tuvieron compra: {fila.top_subieron} subieron, {fila.top_bajaron} bajaron, {fila.top_sin_cambio} sin cambio.
        Gasto del top: {fmtMoneda(fila.importe_top, 0)}.
      </div>
      {vPrev !== null && <div style={s.kSub}>Mes anterior ({nombreMes(previo.mes)}): {fmtVar(vPrev)}</div>}
    </div>
  );
}

function TarjetaFueraTop({ fila, onVer }) {
  const subieron = Number(fila.fuera_subieron) || 0;
  const v = num(fila.variacion_fuera_top);
  return (
    <div style={{ ...s.kcard, borderTop: `3px solid ${subieron > 0 ? COLOR.amarillo : COLOR.verde}` }}>
      <div style={s.kLbl}>Fuera del top · {nombreMes(fila.mes)}</div>
      <div style={{ ...s.kVal, fontSize: 32, color: subieron > 0 ? COLOR.amarillo : COLOR.verde }}>{subieron}</div>
      <div style={{ ...x.veredicto, color: COLOR.gris }}>artículo{subieron === 1 ? '' : 's'} subieron de precio</div>
      <div style={s.kSub}>
        De {fila.fuera_con_compra} artículos con compra fuera del top ({fila.fuera_bajaron} bajaron). Variación ponderada: {fmtVar(v)}.
      </div>
      <button style={{ ...s.linkChico, marginTop: 8, padding: 0 }} onClick={onVer}>Ver cuáles</button>
    </div>
  );
}

function TarjetaEnCurso({ fila }) {
  const hoy = new Date();
  return (
    <div style={{ ...s.kcard, borderTop: `3px dashed ${COLOR.gris}` }}>
      <div style={s.kLbl}>En curso · {nombreMes(fila.mes)} <Tag tipo="amarillo">parcial, día {hoy.getDate()}</Tag></div>
      <div style={{ ...s.kVal, fontSize: 32, color: '#2A1830' }}>{fmtVar(fila.variacion_top)}</div>
      <div style={s.kSub}>
        {Number(fila.top_con_compra) > 0
          ? `${fila.top_con_compra} artículos del top con compra hasta hoy.`
          : 'Todavía no hay compras del top este mes.'}{' '}
        Fuera del top: {fila.fuera_subieron} subieron.
      </div>
    </div>
  );
}

// ─── Selector de artículo (catálogo + búsqueda) ───
function SelectorArticulo({ catalogo, seleccionado, onElegir }) {
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);

  async function buscar() {
    if (!q.trim()) return setResultados([]);
    try {
      const res = await api.get(`${BASE}/articulos-comprados`, { params: { q: q.trim() } });
      setResultados(res.data.data);
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', position: 'relative' }}>
      <select
        style={s.filtroInput}
        value={seleccionado && catalogo.some((c) => c.id_articulo === seleccionado.id_articulo) ? seleccionado.id_articulo : ''}
        onChange={(e) => { const c = catalogo.find((k) => String(k.id_articulo) === e.target.value); if (c) onElegir({ id_articulo: c.id_articulo, articulo: c.articulo }); }}
      >
        <option value="">{seleccionado ? seleccionado.articulo : 'Elige del top…'}</option>
        {catalogo.filter((c) => c.activo).map((c) => <option key={c.id_articulo} value={c.id_articulo}>{c.articulo}</option>)}
      </select>
      <input
        type="text"
        placeholder="u otro artículo…"
        style={{ ...s.filtroInput, width: 150 }}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }}
      />
      <button style={s.botonBuscar2} onClick={buscar}>Buscar</button>
      {resultados.length > 0 && (
        <div style={x.dropdown}>
          {resultados.map((r) => (
            <div key={r.id_articulo} style={x.dropdownItem} onClick={() => { onElegir({ id_articulo: r.id_articulo, articulo: r.articulo }); setResultados([]); setQ(''); }}>
              {r.articulo} <span style={x.unidad}>{r.clase}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Catálogo del top ───
function Catalogo({ catalogo, onCambio, setMensaje }) {
  const [edicion, setEdicion] = useState({});
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);
  const [prioridadNueva, setPrioridadNueva] = useState('ALTA');

  const valor = (c, campo) => (edicion[c.id_articulo]?.[campo] ?? c[campo]);
  const editar = (c, campo, v) => setEdicion({ ...edicion, [c.id_articulo]: { ...edicion[c.id_articulo], [campo]: v } });

  function errorTexto(err) {
    return err.response?.status === 403 ? 'No tienes permiso de captura en Compras.' : err.response?.data?.error || 'No se pudo guardar.';
  }

  async function guardar(c) {
    try {
      await api.put(`${BASE}/catalogo/${c.id_articulo}`, {
        articulo: c.articulo,
        prioridad: valor(c, 'prioridad'),
        orden: valor(c, 'orden'),
        activo: valor(c, 'activo'),
      });
      const resto = { ...edicion };
      delete resto[c.id_articulo];
      setEdicion(resto);
      await onCambio();
      setMensaje({ tipo: 'ok', texto: `${c.articulo}: guardado.` });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: errorTexto(err) });
    }
  }

  async function quitar(c) {
    if (!window.confirm(`¿Quitar "${c.articulo}" del catálogo? Sus compras siguen guardadas; solo deja de contar como top.`)) return;
    try {
      await api.delete(`${BASE}/catalogo/${c.id_articulo}`);
      await onCambio();
      setMensaje({ tipo: 'ok', texto: `${c.articulo} se quitó del catálogo.` });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: errorTexto(err) });
    }
  }

  async function buscar() {
    if (!q.trim()) return setResultados([]);
    try {
      const res = await api.get(`${BASE}/articulos-comprados`, { params: { q: q.trim() } });
      setResultados(res.data.data);
    } catch (err) {
      console.error(err);
    }
  }

  async function agregar(r) {
    try {
      const maxOrden = Math.max(0, ...catalogo.filter((c) => c.prioridad === prioridadNueva).map((c) => c.orden || 0));
      await api.put(`${BASE}/catalogo/${r.id_articulo}`, { articulo: r.articulo, prioridad: prioridadNueva, orden: maxOrden + 1, activo: true });
      setResultados([]);
      setQ('');
      await onCambio();
      setMensaje({ tipo: 'ok', texto: `${r.articulo} se agregó como ${prioridadNueva}.` });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: errorTexto(err) });
    }
  }

  const enCatalogo = new Set(catalogo.map((c) => c.id_articulo));

  return (
    <>
      <div style={s.subtitulo}>
        Los artículos de prioridad <strong>ALTA</strong> y activos forman el top de la tarjeta principal. MEDIA y BAJA se muestran en la tabla del top
        con su etiqueta, pero cuentan como "fuera del top".
      </div>

      <div style={s.tablaScroll}>
        <table style={s.tabla}>
          <thead>
            <tr>
              <th style={s.th}>Artículo</th>
              <th style={s.th}>ID NetSuite</th>
              <th style={s.th}>Prioridad</th>
              <th style={s.th}>Orden</th>
              <th style={s.th}>Activo</th>
              <th style={s.th}>Última compra</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {catalogo.map((c) => {
              const cambiado = !!edicion[c.id_articulo];
              return (
                <tr key={c.id_articulo} style={!valor(c, 'activo') ? { opacity: 0.55 } : undefined}>
                  <td style={{ ...s.td, whiteSpace: 'normal' }}>{c.articulo}</td>
                  <td style={{ ...s.td, color: COLOR.gris }}>{c.id_articulo}</td>
                  <td style={s.td}>
                    <select style={s.inputChico} value={valor(c, 'prioridad')} onChange={(e) => editar(c, 'prioridad', e.target.value)}>
                      {['ALTA', 'MEDIA', 'BAJA'].map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td style={s.td}>
                    <input type="number" min="0" style={{ ...s.inputChico, width: 60 }} value={valor(c, 'orden')} onChange={(e) => editar(c, 'orden', e.target.value)} />
                  </td>
                  <td style={s.td}>
                    <input type="checkbox" checked={!!valor(c, 'activo')} onChange={(e) => editar(c, 'activo', e.target.checked)} />
                  </td>
                  <td style={s.td}>{fmtFecha(c.ultima_compra)}</td>
                  <td style={{ ...s.td, display: 'flex', gap: 8 }}>
                    <button style={{ ...s.botonGuardarChico, opacity: cambiado ? 1 : 0.4 }} disabled={!cambiado} onClick={() => guardar(c)}>Guardar</button>
                    <button style={s.botonQuitarFila} onClick={() => quitar(c)} aria-label={`Quitar ${c.articulo}`}><FiTrash2 /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ ...s.tarjeta, marginTop: 16 }}>
        <div style={s.seccionTitulo}>Agregar artículo</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Busca por nombre (artículos que se han comprado)"
            style={{ ...s.filtroInput, minWidth: 280 }}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }}
          />
          <select style={s.filtroInput} value={prioridadNueva} onChange={(e) => setPrioridadNueva(e.target.value)}>
            {['ALTA', 'MEDIA', 'BAJA'].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button style={s.botonBuscar2} onClick={buscar}>Buscar</button>
        </div>
        {resultados.length > 0 && (
          <div style={{ marginTop: 10 }}>
            {resultados.map((r) => (
              <div key={r.id_articulo} style={{ ...s.puntoFila, justifyContent: 'space-between' }}>
                <span>
                  {r.articulo} <span style={x.unidad}>{r.clase} · ID {r.id_articulo} · última compra {fmtFecha(r.ultima_compra)}</span>
                </span>
                {enCatalogo.has(r.id_articulo)
                  ? <span style={{ fontSize: 11, color: COLOR.gris }}>Ya está en el catálogo</span>
                  : <button style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => agregar(r)}><FiPlus /> Agregar como {prioridadNueva}</button>}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

const x = {
  veredicto: { fontSize: 13, fontWeight: 600, margin: '6px 0 6px' },
  unidad: { fontSize: 10.5, color: '#8A7690', fontWeight: 400, marginLeft: 2 },
  tag: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10, marginLeft: 6, whiteSpace: 'nowrap', textTransform: 'none', letterSpacing: 0 },
  tag_gris: { color: '#6E4F80', background: '#F0E9E0' },
  tag_morado: { color: '#fff', background: '#4A1E5C' },
  tag_amarillo: { color: '#7a5417', background: '#F9F0DD' },
  tag_rojo: { color: '#B23A48', background: '#F7E5E7', marginLeft: 0, whiteSpace: 'normal' },
  tag_azul: { color: '#2F5D8A', background: '#E3EDF7' },
  paginacion: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  botonPagina: { width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E6DDD2', background: '#fff', borderRadius: 6, cursor: 'pointer', color: '#4A1E5C' },
  dropdown: { position: 'absolute', top: '100%', right: 0, zIndex: 20, background: '#fff', border: '1px solid #E6DDD2', borderRadius: 8, boxShadow: '0 6px 20px rgba(42,24,48,0.15)', maxHeight: 260, overflowY: 'auto', minWidth: 280 },
  dropdownItem: { padding: '8px 12px', fontSize: 12.5, cursor: 'pointer', borderBottom: '1px solid #F0E9E0' },
};